/**
 * DropZone — the Intake tab's drag-and-drop file store (plan §16.7 WP-95b,
 * ctx-WP-95 PART G2). Owner decision, Jordan, 2026-09-17: "just an upload
 * window that you drag and drop all files into." Dragging onto the zone and
 * the "Choose files" click-to-pick fallback both funnel into one `accept()`:
 * files are filtered for size here (no mime filter — [OWNER] 233), then
 * POSTed through POST /intake/files three at a time ([OWNER] 230: one HTTP
 * request per file, so one bad file never blocks the rest of a batch). Every
 * stored file calls `onStored` immediately — nothing waits for the whole
 * drop to finish landing.
 */
import { useCallback, useRef, useState, DragEvent, ChangeEvent } from 'react';
import { Upload } from 'lucide-react';
import { secondaryBtnCls } from '../../../ui';
import { storeIntakeFile } from '../../api/intake';
import type { FilenameProposal, IntakeItem } from '../../api/intake';

/** Matches routes/contracts/intake.js's uploadMiddleware cap exactly (PART A1/G2). */
const MAX_FILE_BYTES = 25 * 1024 * 1024;
const MAX_CONCURRENT = 3;
const READS_LINE =
  'Parsers read .xlsx, .xlsm, .docx, .doc and .pdf — anything else can still be dropped and reviewed by a person.';

type RowStatus = 'uploading' | 'stored' | 'failed';

interface FileRow {
  id: number;
  file: File;
  status: RowStatus;
  reason?: string;
}

interface Props {
  onStored: (item: IntakeItem, proposal: FilenameProposal) => void;
  disabled?: boolean;
}

/** Client-side size gate, ahead of the network (25 MB, [OWNER] 233 — no mime filter). */
function sizeReason(file: File): string | null {
  if (file.size === 0) return 'This file is empty.';
  if (file.size > MAX_FILE_BYTES) return 'This file is over the 25 MB limit.';
  return null;
}

/** Structural narrowing only (no axios import under src/contracts — ApproveDialog.tsx's pattern). */
function uploadErrorReason(err: unknown): string {
  const res = (err as { response?: { data?: { error?: string } } })?.response;
  return res?.data?.error ?? 'Something went wrong storing this file.';
}

/**
 * `limit` lanes, each pulling the next item off a shared cursor — the "three
 * in flight" the brief pins (PART G2, [OWNER] 230), with no new dependency.
 */
async function runWithConcurrency<T>(items: T[], limit: number, worker: (item: T) => Promise<void>): Promise<void> {
  let cursor = 0;
  async function lane(): Promise<void> {
    for (;;) {
      const i = cursor;
      cursor += 1;
      if (i >= items.length) return;
      await worker(items[i]);
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, () => lane()));
}

export function DropZone({ onStored, disabled = false }: Props) {
  const [rows, setRows] = useState<FileRow[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const nextId = useRef(0);

  const updateRow = useCallback((id: number, patch: Partial<FileRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  const uploadRow = useCallback(
    async (row: FileRow) => {
      try {
        const { item, proposal } = await storeIntakeFile(row.file);
        updateRow(row.id, { status: 'stored', reason: undefined });
        onStored(item, proposal);
      } catch (err) {
        updateRow(row.id, { status: 'failed', reason: uploadErrorReason(err) });
      }
    },
    [onStored, updateRow]
  );

  /** Both the drop path and the click-to-pick path end up here (G2). */
  const accept = useCallback(
    (files: File[]) => {
      if (disabled || files.length === 0) return;
      const toUpload: FileRow[] = [];
      const newRows: FileRow[] = [];
      for (const file of files) {
        const id = nextId.current;
        nextId.current += 1;
        const reason = sizeReason(file);
        if (reason) {
          newRows.push({ id, file, status: 'failed', reason });
        } else {
          const row: FileRow = { id, file, status: 'uploading' };
          newRows.push(row);
          toUpload.push(row);
        }
      }
      setRows((prev) => [...prev, ...newRows]);
      void runWithConcurrency(toUpload, MAX_CONCURRENT, uploadRow);
    },
    [disabled, uploadRow]
  );

  function handleDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setDragOver(true);
  }

  function handleDragLeave(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    accept(Array.from(e.dataTransfer.files));
  }

  function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
    accept(e.target.files ? Array.from(e.target.files) : []);
    e.target.value = '';
  }

  function retry(row: FileRow) {
    updateRow(row.id, { status: 'uploading', reason: undefined });
    void uploadRow(row);
  }

  return (
    <div className="space-y-3">
      <div
        data-testid="cta-intake-dropzone"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`rounded-xl border-2 border-dashed p-6 text-center transition-colors duration-150 ${
          dragOver
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
            : 'border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800'
        }`}
      >
        <Upload className="mx-auto mb-2 h-8 w-8 text-gray-400 dark:text-gray-500" />
        <p className="text-sm text-gray-700 dark:text-gray-300">Drop files here to store them.</p>
        <p className="mt-2">
          <button type="button" disabled={disabled} onClick={() => inputRef.current?.click()} className={secondaryBtnCls}>
            Choose files
          </button>
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          disabled={disabled}
          data-testid="cta-intake-file-input"
          aria-label="Choose files to store"
          onChange={handleInputChange}
          className="hidden"
        />
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">{READS_LINE}</p>
      </div>

      {rows.length > 0 && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Files dropped this session</p>
            <button
              type="button"
              onClick={() => setRows([])}
              className="rounded px-1 text-xs text-gray-500 underline decoration-dotted hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Clear
            </button>
          </div>
          <ul aria-live="polite" className="space-y-1">
            {rows.map((row) => (
              <li
                key={row.id}
                data-testid={`cta-intake-file-row-${row.id}`}
                className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-1.5 text-sm dark:border-gray-700"
              >
                <span className="min-w-0 flex-1 truncate text-gray-700 dark:text-gray-300">{row.file.name}</span>
                {row.status === 'uploading' && (
                  <span className="flex-shrink-0 text-xs font-medium text-primary-700 dark:text-primary-400">Uploading…</span>
                )}
                {row.status === 'stored' && (
                  <span className="flex-shrink-0 text-xs font-medium text-green-700 dark:text-green-400">Stored</span>
                )}
                {row.status === 'failed' && (
                  <span className="flex flex-shrink-0 items-center gap-2">
                    <span className="text-xs font-medium text-red-700 dark:text-red-400">Failed — {row.reason}</span>
                    <button
                      type="button"
                      onClick={() => retry(row)}
                      className="rounded px-1.5 py-0.5 text-xs font-medium text-primary-700 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:bg-primary-900/20"
                    >
                      Retry
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default DropZone;
